import _ from 'underscore';

import { WebClientListResourceFetcher } from '~src/commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';

import { IUpdateScheduleParams, UpdateScheduleRequest } from '../../backgroundJob/types/UpdateScheduleRequest';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { CreateScheduleRequest, ICreateScheduleParams, ICreateScheduleResult } from '../../schedule/types/CreateScheduleRequest';
import { DeleteSchedulesRequest } from '../../schedule/types/DeleteSchedulesRequest';
import { GetSchedulesRequest, IGetSchedulesResult } from '../../schedule/types/GetSchedulesRequest';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { Filter } from '../helpers/Filter';
import { RunSchedulesRequest } from '../ServerApi/requests/RunSchedulesRequest';
import { RunServerSchedulesRequest } from '../ServerApi/requests/RunServerSchedulesRequest';
import { SetEnabledLinkedTaskStatusForSchedulesRequest } from '../ServerApi/requests/SetEnabledLinkedTaskStatusForSchedulesRequest';
import { SetEnabledStatusForSchedulesRequest } from '../ServerApi/requests/SetEnabledStatusForSchedulesRequest';
import { SetLinkedTasksAllowedRequest } from '../ServerApi/requests/SetLinkedTasksAlowedRequest';
import { SetScheduleNameRequest } from '../ServerApi/requests/SetScheduleNameRequest';
import { FilterFieldNames, IValuedFieldFilterClause } from '../ServerApi/types/IFilterClause';
import { Resource } from './Resource';

export class Schedules extends Resource<IScheduleInfo> {

  public static readonly instance = new Schedules(new WebClientListResourceFetcher(GetSchedulesRequest, (result: IGetSchedulesResult) => result.schedules.map((schedule) => {
            return {
              ...schedule,
              contentType: 'schedule'
            };
          })));

  public filterByActionType(scheduledActionType: string): IValuedFieldFilterClause<string> { // ScheduleActions
    return Filter.fieldEq(FilterFieldNames.scheduledAction, scheduledActionType);
  }

  public getByIdForSite(id: string, siteId?: string) {
    return this.getById(id, _(siteId).isString() ? [ Filter.fieldEq(FilterFieldNames.siteId, siteId) ] : []);
  }

  private setEnabledStatus(ids: string[], status: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetEnabledStatusForSchedulesRequest(idsBlock, status)),
      ResultHelper.combineResults);
  }

  private setEnabledLinkedTaskStatus(ids: string[], status: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetEnabledLinkedTaskStatusForSchedulesRequest(idsBlock, status)),
      ResultHelper.combineResults);
  }

  public disable(ids: string[]): Promise<IResult> {
    return this.setEnabledStatus(ids, false);
  }

  public enable(ids: string[]): Promise<IResult> {
    return this.setEnabledStatus(ids, true);
  }

  public enableLinkedTask(ids: string[]): Promise<IResult> {
    return this.setEnabledLinkedTaskStatus(ids, true);
  }

  public disableLinkedTask(ids: string[]): Promise<IResult> {
    return this.setEnabledLinkedTaskStatus(ids, false);
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteSchedulesRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public runServer(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunServerSchedulesRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public run(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunSchedulesRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public create(schedule: ICreateScheduleParams): Promise<ICreateScheduleResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new CreateScheduleRequest(schedule));
  }

  public update(schedule: IUpdateScheduleParams): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateScheduleRequest(schedule));
  }

  public rename(id: string, newName: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetScheduleNameRequest(id, newName));
  }

  public setLinkedTasksAllowed(scheduleIds: string[], shouldAllowLinkedTasks: boolean) {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetLinkedTasksAllowedRequest({scheduleIds, shouldAllowLinkedTasks}));
  }

}

export const SchedulesService = Schedules.instance;
